import classNames from 'classnames';
import Cookies from 'js-cookie';
import React from 'react';
import { useSelector, useDispatch, connect } from 'react-redux';

import { AccountIcon, ArrowDownIcon } from '@app/components/icons';
import { LogoutButton } from '@app/components/logout-button';
import { useMergeState } from '@app/hooks';
import { updateQuery } from '@app/store/actions/app.actions';

import { Autocomplete } from './algolia-autocomplete';
import {
	MarketingTemplate,
	getMarketingTemplateConfig,
	marketingItems,
} from './marketing-template';
import { ProductItem } from './product-item';
import { ModesRadioButton } from './radio-buttons';

const MobileUtilityNavRaw = ({ auth, authText, searchModes }) => {
	const initialState = {
		isNavOpen: false,
		flyoutListHeight: 0,
	};
	const [state, mergeState] = useMergeState(initialState);
	const flyoutListRef = React.useRef();
	const searchFormRef = React.useRef();

	const { isNavOpen, flyoutListHeight } = state;
	const {
		myCoursesUrl,
		myGradesUrl,
		progressTrackerUrl,
		myOrderHistoryUrl,
		logoutUrl,
		memberSite,
		loginUrl,
		isAuthenticated,
	} = auth;

	const dispatch = useDispatch();
	// eslint-disable-next-line @typescript-eslint/no-shadow
	const selectedModes = useSelector((state) => state.app.selectedModes);
	// eslint-disable-next-line @typescript-eslint/no-shadow
	const currentQuery = useSelector((state) => state.app.query);

	React.useEffect(() => {
		const headerNavToggle = document.querySelector('[data-header-nav=toggle]');
		headerNavToggle.addEventListener('mousedown', () =>
			mergeState({ isNavOpen: false }),
		);

		return () => headerNavToggle.removeEventListener('mousedown');
	}, [mergeState]);

	const calculateFlyoutPanelHeight = React.useCallback(() => {
		const children = flyoutListRef.current.childNodes;
		const totalHeight = Array.from(children)
			.map((node) => node.clientHeight)
			.reduce((a, b) => a + b, 0);

		mergeState({
			flyoutListHeight: totalHeight,
		});
	}, [mergeState]);

	const toggleUtilityNav = () => {
		if (flyoutListHeight === 0) {
			calculateFlyoutPanelHeight();
		}

		mergeState((prevState) => ({ isNavOpen: !prevState.isNavOpen }));
	};
	const [selectedMode, setSelectedMode] = React.useState(
		searchModes.find((mode) => mode.id === selectedModes[0]),
	);
	React.useEffect(() => {
		const cookieSelectedMode = Cookies.get('selectedMode');
		const newSelectedMode = searchModes.find(
			(mode) => mode.id === cookieSelectedMode || mode.id === selectedModes[0],
		);

		if (newSelectedMode) {
			if (cookieSelectedMode) {
				setSelectedMode(newSelectedMode);
			}
		}
	}, [selectedMode, searchModes, selectedModes]);

	const handleButtonClick = () => {
		if (isAuthenticated) {
			toggleUtilityNav();
		} else {
			window.location = loginUrl;
		}
	};

	const handleSearchSubmit = (e) => {
		e.preventDefault();

		const query = e.target.search.value;

		if (!query || !selectedMode || !searchFormRef.current) {
			return false;
		}

		dispatch(updateQuery(query));
		Cookies.set('searchQuery', query, { secure: true });

		// Check if current page is a search page
		const isSearchPage = window.location.href.includes(selectedMode.action);

		if (!isSearchPage) {
			// If not on a search page, redirect to the correct search page with the query
			window.location.href = `${selectedMode.action}?q=${query}`;
		} else {
			// If already on a search page, close the menu after executing a search
			mergeState({ isNavOpen: false });
			// Stay on the page
			window.location.href = `${window.location.pathname}?q=${query}`;
		}

		return false;
	};

	const autocompleteTemplates = {
		// eslint-disable-next-line react/prop-types
		item: ({ item, components }) => (
			<ProductItem hit={item} components={components} />
		),
		components: {},
	};

	const items = [
		{
			title: 'My Account',
			url: '/profile',
		},
		{
			title: 'My Courses & Exams',
			url: `${myCoursesUrl}`,
		},
		{
			title: 'My Progress Tracker',
			url: `${progressTrackerUrl}`,
		},
		{
			title: 'My Grades',
			url: `${myGradesUrl}`,
		},
		{
			title: 'My Order History',
			url: `${myOrderHistoryUrl}`,
		},
	];

	React.useEffect(() => {
		if (flyoutListRef.current) {
			calculateFlyoutPanelHeight();
		}
	}, [flyoutListRef, calculateFlyoutPanelHeight]);

	return (
		<>
			<button
				type="button"
				className={classNames(
					'utility-nav__link',
					'utility-nav__link--btn',
					'utility-nav__link--btn--mobile',
					{
						'is-open': isNavOpen,
					},
				)}
				aria-label="Mobile Authentication Button"
				onClick={handleButtonClick}
			>
				<AccountIcon className="utility-nav__icon" />
				<span className="u-text-uppercase utility-nav__label">
					{authText}
					{isAuthenticated && <ArrowDownIcon />}
				</span>
			</button>

			{isAuthenticated && (
				<div
					className="flyout__panel flyout__panel--react"
					style={{ height: isNavOpen ? `${flyoutListHeight}px` : 0 }}
				>
					<nav role="navigation" aria-label="utility navigation">
						<ul ref={flyoutListRef} className="u-list-unstyled">
							{items.map((item) => (
								<li key={item.url} className="flyout__item">
									<a href={item.url} className="flyout__link">
										{item.title}
									</a>
								</li>
							))}
							<li className="flyout__item">
								<LogoutButton logoutUrl={logoutUrl} memberSite={memberSite} />
							</li>
						</ul>
					</nav>
				</div>
			)}
			{searchModes && searchModes.length && (
				<div className="utility-nav__link">
					<form
						onSubmit={handleSearchSubmit}
						className="mobile-search-form"
						ref={searchFormRef}
					>
						<ModesRadioButton searchModes={searchModes} />
						<div className="mobile-search-form__search-field input__wrapper">
							<label htmlFor="mobile-utility-search">
								Enter your search term
							</label>
							<Autocomplete
								openOnFocus
								selectedMode={selectedMode}
								currentQuery={currentQuery}
								dispatch={dispatch}
								getSources={() => [
									{
										sourceId: 'products',
										getItems() {
											return [];
										},
										templates: autocompleteTemplates,
									},
									getMarketingTemplateConfig(
										'marketing_template',
										marketingItems,
										{ item: MarketingTemplate },
									),
								]}
								className="utility-search__input"
								type="search"
								id="mobile-utility-search"
								name="search"
							/>
						</div>
					</form>
				</div>
			)}
		</>
	);
};

const mapStateToProps = (state) => ({
	auth: state.auth,
});

export const MobileUtilityNav = connect(mapStateToProps)(MobileUtilityNavRaw);
